import React, { useCallback, useEffect, useState } from "react";
import Routes from "../../routes";
import {
    BreadCrumbs,
    PageTitleWithToolTip,
    DataGridWithExpansionPanel,
    ButtonWithIcon,
} from "../Common";
import { Grid, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableRow, Tooltip } from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useNavigate } from "../../hooks";
import { dataGridContainerProps, select_commonCellProps, text_commonCellProps } from "../Common/DataGrid/utils";
import { StyledCellLink } from "../Common/DataGrid/CellComponents";
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { getDateRangeFilterOperator } from "../Common/DataGrid/FilterComponents";
import {GRID_DETAIL_PANEL_TOGGLE_COL_DEF, GridActionsCellItem } from "@mui/x-data-grid-pro";
import { fetchFilterValues } from "../../utils";
import { Grey2 } from "../../color_utils";

const accountTypes = {
    1: "System Owner",
    2: "Contractor Lite",
    4: "SF6",
    8: "Trakref Pro",
    16: "Reclaim",
};

const CustomDetailPanelToggle = ({ row, handleSubtableOpen, expandedRowIds }) => (
    <Tooltip
        title={
            expandedRowIds.includes(row.id)
                ? "Hide Assets by Type"
                : "View Assets by Type"
        }
    >
        <IconButton
            size="medium"
            tabIndex={-1}
            onClick={() => handleSubtableOpen(row)}
        >
            <ExpandMoreIcon
                sx={(theme) => ({
                    color: Grey2,
                    transform: `rotateZ(${expandedRowIds.includes(row.id) ? 180 : 0}deg)`,
                    transition: theme.transitions.create('transform', {
                        duration: theme.transitions.duration.complex
                    }),
                })}
                fontSize="inherit"
            />
        </IconButton>
    </Tooltip>
);

const dateRangeFilterOperator = getDateRangeFilterOperator({});

const ManageAccounts = ({}) => {
    const pageTitle = "Manage Accounts";
    const [filters, setFilters] = useState([]);
    const [expandedRowIds, setExpandedRowIds] = useState([]);

    const navigate = useNavigate({
        pageName: 'ManageAccounts'
    });

    const fetchData = async () => {
        const data = await fetchFilterValues(Routes.Instance.ManageAccountsTableFilters());
        setFilters(data);
    };

    const handleAddNewAccount = () => {
        navigate.push("/ManageAccounts/CreateAccount");
    };

    const handleEditAccount = (id) => {
        navigate.push(`/ManageAccounts/EditAccount/${id}`);
    };

    const handleSubtableOpen = ({ id }) => {
        let newExpandedRowIds = [];
        // if user clicks on open row, close row
        if (expandedRowIds.includes(id)) {
            newExpandedRowIds = expandedRowIds.filter(rowId => rowId !== id);
        } else {
            // row is closed, open row
            newExpandedRowIds = [...expandedRowIds, id];
        }

        return setExpandedRowIds(newExpandedRowIds);
    };

    const getDetailPanelContent = useCallback(({ row: { allAssetsByType } }) => {
        if (Array.isArray(allAssetsByType) && allAssetsByType.length > 0) {
            return (
                <TableContainer component={Paper}>
                    <Table>
                        <TableBody>
                            {allAssetsByType.map(a => (
                                <TableRow>
                                    <TableCell sx={{ width: '64%' }}/>
                                    <TableCell colSpan={4}>
                                        {`${a.count} - ${a.name}`}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            );
        }

        return null;
    }, [expandedRowIds.length]);

    useEffect(() => {
        fetchData();
    }, []);

    const columns = [
        {
            field: 'name',
            headerName: 'Account Name',
            ...text_commonCellProps,
            
            renderCell: ({ row: { id, name } }) => (
                <StyledCellLink
                    primaryLink
                    path={`/ManageAccounts/AccountDetails/${id}`}
                    text={name}
                    from={window.location.pathname}
                />
            )
        },
        {
            field: 'id',
            headerName: 'Account ID',
            ...text_commonCellProps,
        },
        {
            field: 'type',
            headerName: 'Account Type',
            ...select_commonCellProps,
            renderCell: ({ row: { type } }) => accountTypes[type] || "Unknown",
            valueOptions: Object.keys(accountTypes).map(k => ({ value: k, label: accountTypes[k] }))
        },
        {
            field: 'createDate',
            headerName: 'Creation Date',
            valueGetter: (date) => date,
            filterOperators: dateRangeFilterOperator,
            flex: 1,
        },
        {
            field: 'totalUsers',
            headerName: 'Total Users',
            ...text_commonCellProps,
        },
        {
            field: 'totalAssets',
            headerName: 'Total Assets',
            ...text_commonCellProps,
        },
        {
            field: 'totalLocations',
            headerName: 'Total Locations',
            ...text_commonCellProps,
        },
        {
            ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
            width: 150,
            renderCell: ({ row, row: { allAssetsByType } }) => {
                const quickActions = [
                    <GridActionsCellItem
                        key="edit-account"
                        icon={
                            <Tooltip
                                title="Edit Account"
                            >
                                <EditOutlinedIcon />
                            </Tooltip>
                        }
                        onClick={() => handleEditAccount(row.id)}
                    />
                ];

                if (Array.isArray(allAssetsByType) && allAssetsByType.length > 0) {
                    quickActions.push(
                        <CustomDetailPanelToggle
                            row={row}
                            handleSubtableOpen={handleSubtableOpen}
                            expandedRowIds={expandedRowIds}
                        />
                    );
                }

                return quickActions;
            }
        }
    ];

    return (
        <>
            <BreadCrumbs
                path={[
                    { text: "Geek Command Center", to: "/GeekCommandCenter" },
                    { text: pageTitle }
                ]}
            />
            <Grid container direction="row">
                <Grid item xs={6} md={6} lg={6}>
                    <PageTitleWithToolTip
                        pageTitle={pageTitle}
                        toolTipText={"Manage existing accounts"} />
                </Grid>
                
                <Grid item xs={6} sx={{ justifyContent: "flex-end", display: "flex" }}>
                    <ButtonWithIcon
                        buttonText={"Add New Account"}
                        onClick={handleAddNewAccount}
                        Icon={AddIcon}
                    />
                </Grid>
                <Grid item {...dataGridContainerProps}>
                    <DataGridWithExpansionPanel
                        url={Routes.Instance.ManageAccountsTable()}
                        columns={columns}
                        exportButtonURL={Routes.Instance.ManageAccountsTableExport()}
                        exportButtonAlt="Export Accounts"
                        filterOptions={filters}
                        getDetailPanelContent={getDetailPanelContent}
                        getDetailPanelHeight={() => "auto"}
                    />
                </Grid>
            </Grid >
        </>
    );
};

export default ManageAccounts;